import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Sun,
  Coffee,
  AlertTriangle,
  ShieldCheck,
  Battery,
  RefreshCw,
  Loader2,
  X,
} from "lucide-react";

const STATUS = {
  on_track: {
    label: "On Track",
    icon: ShieldCheck,
    border: "border-emerald-500/40",
    bg: "bg-gradient-to-br from-emerald-500/10 via-zinc-950 to-zinc-950",
    accent: "text-emerald-300",
    chip: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
  },
  drifting: {
    label: "Drifting",
    icon: AlertTriangle,
    border: "border-amber-500/40",
    bg: "bg-gradient-to-br from-amber-500/10 via-zinc-950 to-zinc-950",
    accent: "text-amber-300",
    chip: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  },
  stalled: {
    label: "Stalled",
    icon: AlertTriangle,
    border: "border-red-500/40",
    bg: "bg-gradient-to-br from-red-500/10 via-zinc-950 to-zinc-950",
    accent: "text-red-300",
    chip: "bg-red-500/15 text-red-200 border-red-500/40",
  },
  recovery_needed: {
    label: "Recover",
    icon: Coffee,
    border: "border-purple-500/40",
    bg: "bg-gradient-to-br from-purple-500/10 via-zinc-950 to-zinc-950",
    accent: "text-purple-300",
    chip: "bg-purple-500/15 text-purple-200 border-purple-500/40",
  },
};

/**
 * Mid-Day Checkpoint Card
 * Only renders between 11:30am and 4:00pm local time — the ADHD drift zone.
 * Calls GET /api/synthesis/midday-check and lets the user log a 1-tap energy pulse.
 */
export default function MiddayCheckpoint() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [submittingPulse, setSubmittingPulse] = useState(false);

  const now = new Date();
  const hourDecimal = now.getHours() + now.getMinutes() / 60;
  const inWindow = hourDecimal >= 11.5 && hourDecimal <= 16.0;

  const todayKey = now.toISOString().slice(0, 10);
  const dismissKey = `niko_midday_dismissed_${todayKey}`;

  const fetchCheck = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/synthesis/midday-check`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!inWindow) return;
    if (sessionStorage.getItem(dismissKey) === "1") {
      setDismissed(true);
      return;
    }
    fetchCheck();
  }, [inWindow, fetchCheck, dismissKey]);

  const dismiss = () => {
    sessionStorage.setItem(dismissKey, "1");
    setDismissed(true);
  };

  const logPulse = async (level) => {
    setSubmittingPulse(true);
    try {
      await axios.post(
        `${API}/synthesis/midday-pulse`,
        { energy_level: level },
        { withCredentials: true }
      );
      toast.success(`Pulse logged: ${level}/5`);
      await fetchCheck();
    } catch (e) {
      toast.error("Could not log pulse");
    } finally {
      setSubmittingPulse(false);
    }
  };

  if (!inWindow || dismissed) return null;
  if (loading && !data) {
    return (
      <Card className="border-zinc-800 bg-zinc-950" data-testid="midday-checkpoint-loading">
        <CardContent className="py-4 flex items-center gap-2 text-sm text-zinc-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking in on your day...
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const style = STATUS[data.status] || STATUS.on_track;
  const Icon = style.icon;

  return (
    <Card
      className={`relative overflow-hidden ${style.border} ${style.bg} border-2`}
      data-testid={`midday-checkpoint-${data.status}`}
    >
      <CardContent className="pt-4 pb-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-lg bg-zinc-900/80 ring-1 ${style.border} flex-shrink-0`}>
              <Sun className={`w-5 h-5 ${style.accent}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
                  Mid-Day Checkpoint
                </span>
                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${style.chip} flex items-center gap-1`} data-testid="midday-status">
                  <Icon className="w-3 h-3" /> {style.label}
                </span>
              </div>
              <div className="text-white text-base font-semibold leading-snug mt-1" data-testid="midday-headline">
                {data.headline}
              </div>
              {data.observation && (
                <div className="text-xs text-zinc-400 mt-1 leading-relaxed" data-testid="midday-observation">
                  {data.observation}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              size="icon"
              variant="ghost"
              onClick={fetchCheck}
              disabled={loading}
              className="h-7 w-7 text-zinc-500 hover:text-white"
              data-testid="midday-refresh"
              title="Re-check"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={dismiss}
              className="h-7 w-7 text-zinc-500 hover:text-white"
              data-testid="midday-dismiss"
              title="Dismiss for today"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Correction */}
        {data.correction && (
          <div className={`rounded-lg bg-zinc-900/60 border ${style.border} px-3 py-2.5`} data-testid="midday-correction">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">One correction</div>
            <div className={`text-sm font-semibold ${style.accent}`}>{data.correction}</div>
          </div>
        )}

        {/* Energy pulse tap */}
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-zinc-800">
          <div className="flex items-center gap-1.5 text-xs text-zinc-400">
            <Battery className="w-3.5 h-3.5" />
            {data.context?.current_energy != null
              ? <>Current pulse: <span className="text-white font-bold">{data.context.current_energy}/5</span></>
              : "Quick pulse:"}
          </div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => {
              const active = data.context?.current_energy === n;
              return (
                <button
                  key={n}
                  onClick={() => logPulse(n)}
                  disabled={submittingPulse}
                  data-testid={`midday-pulse-${n}`}
                  className={`w-7 h-7 rounded-md text-xs font-bold border transition-colors ${
                    active
                      ? "bg-white text-black border-white"
                      : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:bg-zinc-800"
                  } disabled:opacity-50`}
                  title={`${n}/5`}
                >
                  {n}
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
